// A collecting target may have several rows for the same printing. Group those
// rows without changing physical completion or counting the same owned copy twice.
export function stackSetCards(rows) {
  const groups = new Map();
  for (const row of rows) {
    const id = `${row.owner_id || ''}:${row.card_id || row.id}`;
    if (!groups.has(id)) groups.set(id, { ...row, key: id, variant: null, variants: [], variant_prices: {}, owned_variants: {}, collected_variants: {}, collected: row.collected === undefined ? undefined : 0, positions: [], storage: [], inventory_data: [] });
    const group = groups.get(id);
    group.variants = [...new Set([...group.variants, ...(row.variant ? [row.variant] : row.variants || [])])];
    for (const [finish, count] of Object.entries(row.owned_variants || (row.variant ? { [row.variant]: row.owned ?? row.quantity ?? 0 } : {}))) group.owned_variants[finish] = Math.max(group.owned_variants[finish] || 0, count);
    Object.assign(group.variant_prices ||= {}, row.variant_prices || (row.variant ? { [row.variant]: { price_gbp: row.price_gbp } } : {}));
    if (row.collected !== undefined) group.collected = (group.collected || 0) + row.collected;
    Object.assign(group.collected_variants, row.collected_variants || {});
    group.inventory_data.push(...(Array.isArray(row.inventory_data) ? row.inventory_data : row.inventory_data ? [row.inventory_data] : []));
    group.positions = [...new Map([...group.positions, ...(row.positions || [])].map(position => [`${position.location_id}:${position.page}:${position.slot}:${position.variant}`, position])).values()];
    for (const location of row.storage || []) {
      const current = group.storage.find(entry => entry.id === location.id);
      if (current) current.quantity += location.quantity;
      else group.storage.push({ ...location });
    }
  }
  return [...groups.values()].map(group => {
    const owned = Object.values(group.owned_variants).reduce((sum, count) => sum + count, 0);
    return { ...group, owned, elsewhere: Math.max(0, owned - (group.collected || 0)) };
  });
}

export function unstackSetCards(rows) {
  return rows.flatMap(row => row.variant ? [row] : (row.variants?.length ? row.variants : [row.default_variant || 'normal']).map(variant => {
    const owned = row.owned_variants?.[variant] || 0;
    const collected = row.collected_variants?.[variant] ?? (row.collected === undefined ? undefined : 0);
    return { ...row, ...row.variant_prices?.[variant], variant, key: `${row.card_id || row.id}:${variant}`,
      owned, collected, elsewhere: Math.max(0, owned - (collected || 0)),
      positions: (row.positions || []).filter(position => position.variant === variant) };
  }));
}

// Display only: never change the underlying requirements, copies or placements.
export function displayCards(rows, display) {
  if (!display.stack_variants) return unstackSetCards(rows);
  if (!display.separate_stamps) return stackSetCards(rows);
  const split = rows.flatMap(row => {
    if (row.variant) return [row];
    const variants = row.variants || Object.keys(row.variant_prices || {});
    const stamps = variants.filter(finish => finish.includes('~stamp=') || finish === 'wPromo');
    if (!stamps.length) return [row];
    const ordinary = variants.filter(finish => !stamps.includes(finish));
    const restrict = finishes => ({ ...row, variants: finishes,
      collected: row.collected === undefined ? undefined : finishes.reduce((sum, finish) => sum + (row.collected_variants?.[finish] || 0), 0),
      owned_variants: Object.fromEntries(Object.entries(row.owned_variants || {}).filter(([finish]) => finishes.includes(finish))),
      collected_variants: Object.fromEntries(Object.entries(row.collected_variants || {}).filter(([finish]) => finishes.includes(finish))) });
    return [...(ordinary.length ? [restrict(ordinary)] : []), ...unstackSetCards([restrict(stamps)])];
  });
  const result = [], groups = new Map();
  for (const row of split) {
    const stamped = row.variant && (row.variant.includes('~stamp=') || row.variant === 'wPromo');
    const key = `${row.owner_id || ''}:${row.card_id || row.id}:${stamped ? row.variant : 'stack'}`;
    if (!groups.has(key)) { groups.set(key, []); result.push(key); }
    groups.get(key).push(row);
  }
  return result.map(key => {
    const members = groups.get(key);
    if (members[0].variant && (members[0].variant.includes('~stamp=') || members[0].variant === 'wPromo')) return { ...members[0], key };
    return { ...stackSetCards(members)[0], key };
  });
}
